// SportsNewsPage.jsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  CircularProgress,
  Button,
  Container,
  Chip,
  Stack,
  IconButton,
  TextField,
  Link
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn,
  LocationOn,
  Phone,
  Email
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import FallbackContent from '../components/FallbackContent';

const SportsNewsPage = () => {
  const [sportsStories, setSportsStories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Define categories array
  const categories = ['All', 'Politics', 'Business', 'Sports', 'Technology', 'Entertainment'];

  React.useEffect(() => {
    const fetchSportsStories = async () => {
      try {
        const response = await fetch('https://ethioinsider-backend.onrender.com/api/articles/sports');
        if (!response.ok) {
          throw new Error('Failed to fetch sports stories');
        }
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setSportsStories(result.data);
        } else {
          throw new Error('Unexpected API response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSportsStories();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: "center", py: 4, mt: 10 }}>
        Error loading sports stories: {error}
      </Typography>
    );
  }

  if (sportsStories.length === 0) {
    return <FallbackContent category="sports" />;
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4, mt: 10 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
          Sports News
        </Typography>
        
        <Grid container spacing={3}>
          {sportsStories.map((story) => (
                          <Grid xs={12} sm={6} md={4} key={`sports-${story._id}`}>
              <Card square elevation={1}>
                <CardActionArea 
                  component={RouterLink}
                  to={`/articles/${story._id}`}
                  sx={{ height: "100%" }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={story?.imageUrl || "https://via.placeholder.com/400x250?text=Sports"}
                    alt={story?.title}
                    sx={{ objectFit: "cover" }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/400x250?text=Sports";
                    }}
                  />
                  <CardContent>
                    <Chip label="Sports" color="primary" size="small" sx={{ mb: 1 }} />
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "3em"
                      }}
                    >
                      {story?.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {story?.excerpt || "No excerpt available"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {new Date(story?.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

         {/* Footer */}
         <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
                             <Stack spacing={1}>
                 {[
                   { name: "Home", path: "/" },
                   { name: "About Us", path: "/about" },
                   { name: "Contact", path: "/contact" }
                 ].map((item) => (
                   <motion.div whileHover={{ x: 5 }} key={item.name}>
                     <Link
                       to={item.path}
                       style={{
                         display: "block",
                         color: "inherit",
                         textDecoration: "none",
                       }}
                       sx={{
                         color: "text.secondary",
                         "&:hover": { color: "primary.main" },
                       }}
                     >
                       {item.name}
                     </Link>
                   </motion.div>
                 ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      component={RouterLink}
                      to={`/${category.toLowerCase()}-news`}
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        textDecoration: "none",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SportsNewsPage;